/** File name without its final extension, normalized for matching. */
function basename(name) {
  return String(name || '').replace(/\.[^.]+$/, '').toLowerCase()
}

/** Escapes a literal file base before it becomes part of a regular expression. */
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** A package file belongs to the TXT base when it is identical or continues with `_...`. */
export function belongsToMetadata(file_name, metadata_name) {
  const file_base = basename(file_name)
  const metadata_base = basename(metadata_name)
  return !!metadata_base && new RegExp(`^${escapeRegExp(metadata_base)}(?:_|$)`, 'i').test(file_base)
}

/** Stable wizard order: TXT first, JPEGs by name, then every other attachment by name. */
export function sortTzEntries(entries) {
  const rank = entry=>entry.is_meta ? 0 : (entry.is_jpeg ? 1 : 2)
  return [...(entries || [])].sort((a, b)=>{
    const category = rank(a) - rank(b)
    return category || a.name.localeCompare(b.name, undefined, { numeric: true })
  })
}

/** Validates the single-record wizard contract without depending on browser file ordering. */
export function validateSingleTzEntries(entries) {
  const metadata = (entries || []).filter(entry=>entry.is_meta)
  if(metadata.length>1) return { error: 'error.MultipleMetadata', files: metadata.map(file=>file.name) }
  if(metadata.length===0) return null
  const mismatched = entries.filter(entry=>entry.is_jpeg && !belongsToMetadata(entry.name, metadata[0].name))
  return mismatched.length>0 ? { error: 'error.PhotoNameMismatch', files: mismatched.map(file=>file.name) } : null
}

/**
 * Groups dropped files around metadata file names. A file belongs to a TXT when its base is
 * identical to the TXT base or continues with an underscore (record.txt, record_1.JPG,
 * record_2.JPG, record.JMX). The longest matching metadata base wins, so overlapping names do
 * not steal one another's files. The grouping is rebuilt from all entries and is independent of
 * drop order.
 */
export function groupBulkEntries(entries) {
  const groups = {}
  const metadata = (entries || []).filter(entry=>entry.is_meta)
  const metadata_keys = metadata.map(entry=>basename(entry.name)).sort((a, b)=>b.length - a.length)

  for(const entry of metadata) {
    const key = basename(entry.name)
    groups[key] = { ...(groups[key] || {}), meta: entry.data_url, meta_name: entry.name, photos: [], extras: [] }
  }

  for(const entry of (entries || []).filter(item=>!item.is_meta)) {
    const file_base = basename(entry.name)
    const key = metadata_keys.find(meta_base=>new RegExp(`^${escapeRegExp(meta_base)}(?:_|$)`, 'i').test(file_base)) || file_base
    const group = groups[key] || { photos: [], extras: [] }
    if(entry.is_jpeg) group.photos.push(entry)
    else group.extras.push(entry)
    groups[key] = group
  }

  for(const group of Object.values(groups)) {
    group.photos.sort((a, b)=>a.name.localeCompare(b.name, undefined, { numeric: true }))
    group.extras.sort((a, b)=>a.name.localeCompare(b.name, undefined, { numeric: true }))
  }
  return groups
}
